import { Tranche } from "@lib/tranche";
import { Funder } from "./aggregate";

export function trancheListFromIncrementalEvalSteps(
  funder: Funder,
  holdProposalId: string,
): Tranche[] {
  const tranches: Tranche[] = [];
  const truncatedSteps = funder.incrementalEvalSteps.slice(
    0,
    funder.incrementalEvalIndex,
  );
  for (const { evaluableId, incrementAmount } of truncatedSteps) {
    if (incrementAmount > 0) {
      let currentTranche = tranches[tranches.length - 1];
      if (!currentTranche || !(evaluableId in currentTranche.allocations)) {
        currentTranche = {
          height: 0.01,
          allocations: {
            [evaluableId]: {
              amount: 0,
              bottomPlaced: true,
            },
          },
        };
        tranches.push(currentTranche);
      }

      currentTranche.allocations[evaluableId].amount += incrementAmount;
    } else {
      const trancheIndicesToRemove: number[] = [];
      let amountRemaining = Math.abs(incrementAmount);
      for (let i = tranches.length - 1; i >= 0; i--) {
        if (amountRemaining <= 0) {
          break;
        }

        const tranche = tranches[i];
        if (!(evaluableId in tranche.allocations)) {
          continue;
        }

        const allocation = tranche.allocations[evaluableId];
        if (allocation.amount > amountRemaining) {
          allocation.amount -= amountRemaining;
          amountRemaining = 0;
          break;
        } else {
          amountRemaining -= allocation.amount;
          allocation.amount = 0;
          trancheIndicesToRemove.push(i);
        }
      }

      if (amountRemaining > 0) {
        throw new Error(
          `Could not remove all ${incrementAmount}! ${amountRemaining} remaining.`,
        );
      }

      for (const i of trancheIndicesToRemove) {
        tranches.splice(i, 1);
      }
    }
  }

  let totalHeight = tranches.reduce((acc, t) => acc + t.height, 0);
  tranches.push({
    height: 5 - totalHeight,
    allocations: {},
  });

  const unincrementedEvals = {};
  Object.values(funder.evaluations).map((evaluable) => {
    if (
      !tranches.some((tranche) => evaluable.evaluableId in tranche.allocations)
    ) {
      unincrementedEvals[evaluable.evaluableId] = {
        amount: 0,
        bottomPlaced: true,
      };
    }
  });

  tranches.push({
    height: 0,
    allocations: {
      ...unincrementedEvals,
      [holdProposalId]: {
        amount: 1e12,
        bottomPlaced: true,
      },
    },
  });

  return tranches;
}

export function getFunderBudgetFromIncrementalEvalSteps(
  incrementalEvalSteps: {
    evaluableId: string;
    incrementAmount: number;
  }[],
  index: number,
  holdProposalId: string,
): number {
  let budget = 0;
  const truncatedSteps = incrementalEvalSteps.slice(0, index);

  for (const step of truncatedSteps) {
    // currently this is not possible in the UI
    if (step.evaluableId === holdProposalId) {
      continue;
    }

    budget += step.incrementAmount;
  }

  return budget;
}
